<template>
  <RevCard
    :id="`review-${id}`"
    ref="review-card"
    class="space-y-20 p-16 md:space-y-24"
    data-qa="user-comment"
    data-test="review-display"
  >
    <div
      class="flex w-full items-start justify-between"
      :class="{
        'md:flex-row': !forceMobileDisplay,
        'flex-col': !isReviewerInformationEnabled,
      }"
    >
      <div class="w-full" :class="{ 'md:w-auto': !forceMobileDisplay }">
        <div
          class="flex items-center justify-between"
          :class="{
            'md:justify-start': !forceMobileDisplay,
            'mb-4 gap-16': isReviewerInformationEnabled,
            'gap-8': !isReviewerInformationEnabled,
          }"
        >
          <template v-if="isReviewerInformationEnabled">
            <RevPopover
              contentClassNames="z-10"
              position="right-start"
              :shouldShowOnMount="false"
              @close="isPopoverOpen = false"
              @open="isPopoverOpen = true"
            >
              <template #trigger="{ show, hide }">
                <RevButtonBase
                  class="flex px-12 py-9 min-h-[42px] md:max-h-[42px] bg-static-default-mid hover:bg-static-default-mid-hover motion-safe:transition-colors motion-safe:duration-300 rounded-full items-center"
                  @click="handleReviewerInfoMobileModal($event, 'show')"
                  @focusin="handleReviewerInfoPopover(show, hide, 'show')"
                  @focusout="handleReviewerInfoPopover(show, hide, 'hide')"
                  @mouseleave="handleReviewerInfoPopover(show, hide, 'hide')"
                  @mouseover="handleReviewerInfoPopover(show, hide, 'show')"
                >
                  <h3 class="heading-3 mx-6">{{ userName }}</h3>

                  <IconChevronRight />
                </RevButtonBase>
              </template>

              <template #content>
                <ReviewerInfo
                  :country-code
                  info-type="popover"
                  :reviewer-info-pending
                  :reviewerInfo
                  :userName
                />
              </template>
            </RevPopover>

            <RevModalBase
              backdrop-background-color="low"
              background-color="low"
              class="rounded-t-[38px]"
              :name="`${REVIEWER_INFO_MODAL}-${id}`"
              :transition-class-names="{
                enterActiveClass: tw`motion-safe:transition-transform motion-safe:duration-2500 md:motion-safe:transition-opacity`,
                enterFromClass: tw`translate-y-full md:opacity-0`,
                leaveActiveClass: tw`motion-safe:transition-transform motion-safe:duration-2500 md:motion-safe:transition-opacity`,
                leaveToClass: tw`translate-y-full md:opacity-0`,
              }"
            >
              <template #body>
                <ReviewerInfo
                  :country-code
                  info-type="modal"
                  :reviewer-info-pending
                  :reviewerInfo
                  :userName
                />
              </template>
            </RevModalBase>
          </template>

          <h3 v-else class="heading-3">{{ userName }}</h3>

          <span
            class="bg-static-info-mid text-static-info-hi rounded-xs body-2-bold flex max-w-full items-center truncate py-0 pl-2 pr-4"
            :class="{ 'hidden md:flex': isReviewerInformationEnabled }"
          >
            <IconOfficialFilled class="mr-4" size="16" />

            {{ i18n(translations.verifiedPurchase) }}
          </span>
        </div>

        <p
          v-if="reviewInfotext"
          class="text-static-default-low body-2"
          data-allow-mismatch="text"
        >
          {{ reviewInfotext }}
          <Flag
            v-if="countryCode"
            aria-hidden="true"
            class="ml-2 inline-block align-baseline"
            :country-code="countryCode as MarketCountryCode"
          />
        </p>

        <span
          v-if="isReviewerInformationEnabled"
          class="bg-static-info-mid text-static-info-hi rounded-xs body-2-bold max-w-full items-center truncate py-0 pl-2 pr-4 mt-4 flex md:hidden w-fit"
        >
          <IconOfficialFilled class="mr-4" size="16" />

          {{ i18n(translations.verifiedPurchase) }}
        </span>
      </div>

      <div
        class="flex flex-col"
        :class="{ 'md:items-end': !forceMobileDisplay }"
      >
        <RevRating class="mt-12 md:mt-0" :score="averageRate" />
        <template v-if="helpRequest && isHelpRequestStatusEnabled">
          <span
            v-if="helpRequestStatus === 'IN_PROGRESS'"
            class="text-static-info-hi caption caption-bold flex max-w-full items-center"
          >
            <IconCommentPendingFilled class="mr-2" size="20" />

            {{ i18n(translations.helpRequestInProgress) }}
          </span>
          <span
            v-else-if="helpRequestStatus === 'RESOLVED'"
            class="text-static-success-hi caption caption-bold flex max-w-full items-center"
          >
            <IconCommentSolvedFilled class="mr-2" size="20" />

            {{ i18n(translations.helpRequestResolved) }}
          </span>
        </template>
      </div>
    </div>

    <!-- PhotosSlider (or any other component) can be injected here-->
    <slot />

    <div v-if="mainReviewComment" class="flex flex-col gap-8">
      <ReviewText
        :comment="mainReviewComment"
        data-test="main-review-comment"
        :force-expand="expandComment"
        :tags
        :tracking="trackingData.onClick"
      />

      <!-- Translation -->
      <div v-if="isTranslatable" class="flex flex-wrap items-center gap-4">
        <p v-if="isTranslated" class="text-static-default-low caption">
          {{ translatedFrom }}
        </p>

        <RevDivider v-if="isTranslated" class="h-16" variant="vertical" />

        <RevLink
          class="text-static-default-low leading-caption text-caption-bold"
          data-test="toggle-translation-button"
          @click="onToggleTranslation"
        >
          {{ toggleTranslationLabel }}
        </RevLink>
      </div>
    </div>
    <div class="flex flex-col items-end gap-8 md:flex-row md:items-center">
      <ProductDetails
        :backboxGrade="salesOrderItem?.backboxGrade"
        class="w-full flex-1 md:w-auto"
        :display-product-link
        :display-seller
        :force-mobile-display
        :is-translatable
        :product
        :seller
        :tracking="trackingData.onClick"
      />

      <ReviewVoteButton
        v-if="FF_ENABLE_REVIEW_VOTING"
        :review-id="id"
        :tracking="trackingData.onClick"
        :votes-count="voteCount"
      />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import {
  RevModalBase,
  closeModal,
  isModalOpened,
  openModal,
} from '@ds/components/ModalBase'
import { RevPopover } from '@ds/components/Popover'
import { RevRating } from '@ds/components/Rating'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconCommentPendingFilled } from '@ds/icons/IconCommentPendingFilled'
import { IconCommentSolvedFilled } from '@ds/icons/IconCommentSolvedFilled'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'

import {
  useReviewImpressionTracker,
  useTrackReviewImpressionInViewport,
} from '../../composables/useTrackReviewImpression'
import { useTranslateComment } from '../../composables/useTranslateComment'
import type { UserComment } from '../../models/UserComment.type'

import ProductDetails from './ProductDetails/ProductDetails.vue'
import translations from './ReviewDisplay.translations'
import ReviewText from './ReviewText/ReviewText.vue'
import ReviewVoteButton from './ReviewVoteButton/ReviewVoteButton.vue'
import ReviewerInfo from './ReviewerInfo/ReviewerInfo.vue'
import { useFetchReviewerInfo } from './ReviewerInfo/useFetchReviewerInfo'

export type UserCommentProps = UserComment & {
  trackingData?: {
    onClick: Record<string, unknown>
    onImpression: Record<string, unknown>
  }
  trackReviewImpression?: boolean
  displaySeller?: boolean
  forceMobileDisplay?: boolean
  displayProductLink?: boolean
  voteCount?: number
}

const props = withDefaults(defineProps<UserCommentProps>(), {
  trackingData: () => ({
    onClick: {},
    onImpression: {},
  }),
  translatedComment: undefined,
  trackReviewImpression: false,
  seller: undefined,
  displaySeller: false,
  forceMobileDisplay: false,
  displayProductLink: true,
  voteCount: 0,
  tags: undefined,
})

const i18n = useI18n()
const { trackClick, trackReviewerInfoImpression } = useTracking()
const route = useRoute()
const experiments = useExperiments()
const { trackExtendedReviewImpression } = useReviewImpressionTracker()
const breakpoint = useClientOnlyComputedBreakpoint()
const {
  data: reviewerInfo,
  pending: reviewerInfoPending,
  execute: getReviewerInfo,
} = useFetchReviewerInfo(props.customer.id)

const reviewRef = useTemplateRef<HTMLElement>('review-card')
const expandComment = ref(false)
const isPopoverOpen = ref(false)
let hasTrackedReviewerInfo = false

const { FF_ENABLE_REVIEW_VOTING } = useRuntimeConfig().public

const isHelpRequestStatusEnabled =
  experiments?.['experiment.reviewsHelpRequestStatus'] ===
  'withHelpRequestStatus'

const isReviewerInformationEnabled =
  experiments?.['experiment.reviewsReviewerInformation'] ===
  'withReviewerInformation'

const REVIEWER_INFO_MODAL = 'REVIEWER_INFO_MODAL'

const {
  mainReviewComment,
  isTranslated,
  toggleTranslation,
  isTranslatable,
  toggleTranslationLabel,
  translatedFrom,
} = useTranslateComment(props)

let showTimeout: ReturnType<typeof setTimeout> | undefined
let hideTimeout: ReturnType<typeof setTimeout> | undefined

watch(
  () => props.translatedComment,
  (newTranslatedComment) => {
    if (newTranslatedComment) {
      expandComment.value = true
    }
  },
)

const userName = computed(() => {
  if (props.customer.firstName && props.customer.lastName) {
    return `${props.customer.firstName} ${props.customer.lastName}`
  }

  return (
    props.customer.firstName ??
    props.customer.lastName ??
    i18n(translations.anonymousUser)
  )
})

const reviewInfotext = computed(() =>
  props.reviewDate && props.countryCode
    ? i18n(translations.reviewInfo, {
        reviewDate: i18n.date(new Date(props.reviewDate), {
          dateStyle: 'short',
        }),
        country: i18n.country(props.countryCode),
      })
    : '',
)

const helpRequestStatus = computed(() => props.helpRequest?.status)

async function onToggleTranslation() {
  trackClick({
    name: isTranslated.value ? 'see_original' : 'translate',
    ...props.trackingData?.onClick,
  })

  await toggleTranslation()
}

function trackReviewerInfoView() {
  if (hasTrackedReviewerInfo) return
  hasTrackedReviewerInfo = true

  trackReviewerInfoImpression({
    pageType: String(route.name),
    reviewId: props.id,
    reviewerCountry: props.countryCode,
    reviewerCustomerSince: reviewerInfo.value?.customerSince || '',
    reviewerNbPurchase: reviewerInfo.value?.purchaseCount || '',
    reviewerNbReviews: reviewerInfo.value?.reviewCount || '',
    reviewerRatingAvg: reviewerInfo.value?.averageRating || '',
  })
}

async function handleReviewerInfoPopover(
  show: () => Promise<void>,
  hide: () => void,
  action: 'show' | 'hide',
) {
  if (breakpoint.value < Breakpoint.MD) return

  if (action === 'show') {
    clearTimeout(hideTimeout)

    showTimeout = setTimeout(show, 300)

    if (!reviewerInfo.value && !reviewerInfoPending.value) {
      await getReviewerInfo()
      trackReviewerInfoView()
    }

    return
  }

  clearTimeout(showTimeout)
  hideTimeout = setTimeout(hide, 300)
}

async function handleReviewerInfoMobileModal(
  _event?: Event,
  action?: 'show' | 'hide',
) {
  const modalId = `${REVIEWER_INFO_MODAL}-${props.id}`
  const onMobile = breakpoint.value < Breakpoint.MD

  if (action === 'show' && onMobile) {
    openModal(modalId)

    if (!reviewerInfo.value && !reviewerInfoPending.value) {
      await getReviewerInfo()
      trackReviewerInfoView()
    }

    return
  }

  if ((action === 'hide' || !onMobile) && isModalOpened(modalId)) {
    closeModal(modalId)
  }
}

onMounted(() => {
  if (props.trackReviewImpression) {
    useTrackReviewImpressionInViewport(reviewRef, {
      userComment: props,
      isTranslated,
    })
  }

  if (isReviewerInformationEnabled) {
    window.addEventListener('resize', handleReviewerInfoMobileModal, {
      passive: true,
    })
  }
})

onBeforeUnmount(() => {
  if (!isReviewerInformationEnabled) return

  clearTimeout(hideTimeout)
  clearTimeout(showTimeout)

  window.removeEventListener('resize', handleReviewerInfoMobileModal)
})

watch(
  () => props.id,
  () => {
    if (props.trackReviewImpression) {
      trackExtendedReviewImpression({
        userComment: props,
        isTranslated: isTranslated.value,
      })
    }
  },
)
</script>
